/**
 * Înregistrarea service worker-ului.
 *
 * Aplicația avea deja un mecanism de versionare: `VersionContext` interoghează
 * `/version` la 10 secunde și, la nepotrivire, arată popup-ul „versiune nouă” cu un
 * buton de reîmprospătare. Un service worker adaugă un al doilea strat de cache, cu
 * propriul ciclu de viață — iar `window.location.reload()` NU îl ocolește.
 *
 * Fără integrarea de aici, un utilizator ar apăsa butonul, ar primi tot shell-ul vechi
 * din cache și popup-ul ar reapărea la nesfârșit. Într-o aplicație care emite facturi
 * și trimite emailuri către clienți, cineva care rulează frontend vechi peste backend
 * nou nu e un inconvenient de interfață, ci exact tiparul din incidentele documentate.
 *
 * De aceea service worker-ul e înregistrat cu `registerType: 'prompt'` (nu preia
 * controlul singur), iar preluarea se face doar la comanda utilizatorului, prin
 * `activateUpdateAndReload()`, apelată din `hardRefresh`.
 */

import { registerSW } from 'virtual:pwa-register';

/** Cât așteptăm un service worker nou să intre în „waiting” înainte de a reîncărca oricum. */
const UPDATE_WAIT_MS = 4000;

const CHUNK_RELOAD_KEY = 'tls.chunkReloadAt';

/**
 * Un tab deschis pe un build vechi cere, la navigare, chunk-uri lazy ale căror hash-uri au
 * dispărut după deploy. Vite semnalează asta prin `vite:preloadError`. Reîncărcăm pagina o
 * singură dată (shell-ul vine de la rețea, cu referințele noi); dacă și după reîncărcare
 * pică, lăsăm eroarea să ajungă în consolă, ca să nu intrăm într-o buclă.
 */
export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    let lastReload = 0;
    try {
      lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    } catch {
      /* sessionStorage indisponibil — reîncărcăm oricum o dată */
    }
    if (Date.now() - lastReload < 60_000) return;
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      /* ignorăm */
    }
    event.preventDefault();
    window.location.reload();
  });
}

let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | null = null;
let registration: ServiceWorkerRegistration | null = null;
let hasWaitingUpdate = false;

export function initServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      // Nu arătăm nimic aici: popup-ul de versiune al aplicației e singurul canal
      // prin care utilizatorul află că trebuie să reîmprospăteze.
      hasWaitingUpdate = true;
    },
    onRegisteredSW(_swUrl, reg) {
      registration = reg ?? null;
    },
    onRegisterError(error) {
      console.warn('Service worker registration failed:', error);
    },
  });
}

/** Există deja o versiune nouă descărcată, care așteaptă să preia controlul? */
export function isUpdateWaiting(): boolean {
  return hasWaitingUpdate || !!registration?.waiting;
}

/**
 * Cere service worker-ului să caute o versiune nouă.
 * Apelat când `/version` raportează altă versiune decât cea din bundle-ul curent,
 * ca service worker-ul nou să fie deja descărcat până apasă utilizatorul butonul.
 */
export async function checkForServiceWorkerUpdate(): Promise<void> {
  try {
    await registration?.update();
  } catch (error) {
    console.warn('Service worker update check failed:', error);
  }
}

/**
 * Aplică versiunea nouă și reîncarcă pagina.
 *
 * Reîncarcă întotdeauna, chiar dacă nu există service worker sau update în așteptare —
 * butonul din popup trebuie să facă ceva de fiecare dată.
 */
export async function activateUpdateAndReload(): Promise<void> {
  const reload = () => window.location.replace(window.location.href);

  if (!('serviceWorker' in navigator) || !updateServiceWorker) {
    reload();
    return;
  }

  try {
    // Dacă nu s-a semnalat încă un update, mai dăm o șansă căutării: popup-ul apare pe
    // baza răspunsului de la /version, care poate ajunge înaintea service worker-ului.
    if (!isUpdateWaiting()) {
      await checkForServiceWorkerUpdate();
      await waitForWaitingWorker(UPDATE_WAIT_MS);
    }

    if (isUpdateWaiting()) {
      // `skipWaiting` + reload la `controllerchange`, gestionate de vite-plugin-pwa.
      await updateServiceWorker(true);
      // Plasă de siguranță: dacă `controllerchange` nu vine, reîncărcăm noi.
      window.setTimeout(reload, UPDATE_WAIT_MS);
      return;
    }
  } catch (error) {
    console.warn('Service worker update failed, se reîncarcă oricum:', error);
  }

  reload();
}

/* -------------------------------------------------------------------------- */
/*  Instalarea aplicației (butonul din pagina de login)                        */
/* -------------------------------------------------------------------------- */

/**
 * Evenimentul prin care Chrome/Edge anunță că aplicația poate fi instalată.
 * Nu e încă în tipurile standard de DOM.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

function notifyInstallListeners() {
  installListeners.forEach((listener) => listener());
}

/**
 * `beforeinstallprompt` se declanșează o singură dată și adesea ÎNAINTE ca React să
 * apuce să monteze ceva. De aceea îl prindem aici, la încărcarea modulului, nu într-un
 * `useEffect` — altfel butonul de instalare n-ar apărea decât din întâmplare.
 *
 * Safari pe iPhone nu emite niciodată evenimentul (instalarea se face manual, din
 * „Share → Add to Home Screen”), deci acolo butonul rămâne ascuns de la sine.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Fără `preventDefault`, unele browsere afișează propriul indicator de instalare.
    event.preventDefault();
    installPrompt = event as BeforeInstallPromptEvent;
    notifyInstallListeners();
  });

  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    notifyInstallListeners();
  });
}

/** Rulează deja ca aplicație instalată (fereastră proprie)? Atunci nu mai oferim instalarea. */
export function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari (iOS) folosește o proprietate proprie, nedeclarată în tipurile standard.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Browserul a confirmat că aplicația poate fi instalată acum? */
export function canInstallApp(): boolean {
  return installPrompt !== null && !isRunningStandalone();
}

/** Abonare la schimbările de disponibilitate. Întoarce funcția de dezabonare. */
export function onInstallAvailabilityChange(listener: () => void): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}

/**
 * Deschide dialogul de instalare al browserului.
 * Trebuie apelată dintr-un gest al utilizatorului (click), altfel browserul o refuză.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!installPrompt) return 'unavailable';

  try {
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    // Evenimentul nu poate fi refolosit: browserul emite altul dacă mai e cazul.
    installPrompt = null;
    notifyInstallListeners();
    return outcome;
  } catch (error) {
    console.warn('Install prompt failed:', error);
    installPrompt = null;
    notifyInstallListeners();
    return 'unavailable';
  }
}

/** Așteaptă până când un service worker nou ajunge în „waiting”, sau până expiră timpul. */
function waitForWaitingWorker(timeoutMs: number): Promise<void> {
  if (isUpdateWaiting()) return Promise.resolve();

  return new Promise((resolve) => {
    const installing = registration?.installing;
    let settled = false;

    const done = () => {
      if (settled) return;
      settled = true;
      installing?.removeEventListener('statechange', onStateChange);
      resolve();
    };

    function onStateChange() {
      if (installing?.state === 'installed' || installing?.state === 'activated') done();
    }

    installing?.addEventListener('statechange', onStateChange);
    window.setTimeout(done, timeoutMs);
  });
}
